import { Button, Checkbox, Container, Grid, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { FiatCurrency } from 'src/common/static'
import * as S from '../styles'
import { SummaryTableCard } from 'src/ui/SummaryTable'
import { BankAccountModel } from 'src/common/api/banking/models'
import { getBanksByIds } from 'src/common/api/banking/data'

interface PaymentInstructionsProps {
  amount: string | number
  fiatCurrency: FiatCurrency
  selectedBankId: string
  isSmallScreen?: boolean
  onProceed: () => void
}

const PaymentInstructions: React.FC<PaymentInstructionsProps> = ({
  amount,
  fiatCurrency,
  selectedBankId,
  isSmallScreen,
  onProceed
}: PaymentInstructionsProps) => {
  const [checked, setChecked] = useState(false)
  const [bankAccount, setBankAccount] = useState<BankAccountModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked)
  }

  useEffect(() => {
    const fetchBankInstructions = async () => {
      try {
        const bankResponse = await getBanksByIds(selectedBankId)
        const selectedBank = Array.isArray(bankResponse) ? bankResponse[0] : bankResponse
        setBankAccount(selectedBank ?? null)
      } catch (fetchError) {
        setError('Unable to load payment instructions. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    if (selectedBankId) {
      fetchBankInstructions()
    } else {
      setLoading(false)
    }
  }, [selectedBankId])

  const bankSummaryRows = useMemo(() => {
    if (!bankAccount) {
      return []
    }

    return Array.isArray(bankAccount.bankDetails)
      ? bankAccount.bankDetails.flatMap((detail) => {
          if (!detail || typeof detail !== 'object') {
            return []
          }

          return Object.entries(detail).map(([section, detailRows]) => {
            const rows = Array.isArray(detailRows)
              ? detailRows.map((row) => ({
                  key: String((row as Record<string, unknown>).key ?? ''),
                  value: String((row as Record<string, unknown>).value ?? '')
                }))
              : detailRows && typeof detailRows === 'object'
              ? Object.entries(detailRows).map(([key, value]) => ({
                  key,
                  value: value == null ? '' : String(value)
                }))
              : [{ key: section, value: String(detailRows ?? '') }]

            return {
              title: section,
              rows
            }
          })
        })
      : []
  }, [bankAccount])

  const isTestEnv =
    process.env.REACT_APP_API_URL?.includes('staging') ||
    process.env.REACT_APP_API_URL?.includes('sandbox') ||
    process.env.REACT_APP_API_URL?.includes('localhost')

  return (
    <Stack spacing={3} sx={{ maxWidth: '100%', width: '100%' }}>
      <S.Row>
        <Typography variant="h5" fontWeight={600}>
          Transfer Details
        </Typography>
        <Typography variant="subtitle1" color="grey.700">
          Please complete the funds transfer to {isTestEnv ? 'QCAD Digital Trust (Pending)' : 'VersaBank'}, account
          details as follows:
        </Typography>
      </S.Row>
      <S.Row>
        <Container sx={{ backgroundColor: 'grey.100', borderRadius: 2, padding: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="h6" fontWeight={600}>
                Payment Details
              </Typography>
              <Typography variant="subtitle1" color="grey.700">
                Amount (CAD)
              </Typography>
            </Grid>
            <Grid item textAlign="right" xs={8}>
              <Typography variant={isSmallScreen ? 'h5' : amount.toString().length > 10 ? 'h4' : 'h3'} fontWeight={600}>
                ${amount} {fiatCurrency}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </S.Row>

      <S.Row>
        <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
          {loading && (
            <Grid item xs={12}>
              <Typography variant="body1">Loading payment instructions…</Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            </Grid>
          )}

          {!loading && !error && bankSummaryRows.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body1">No payment instructions are available.</Typography>
            </Grid>
          )}

          {!loading && !error
            ? bankSummaryRows.map((bank) => (
                <Grid item xs={12} md={isSmallScreen ? 12 : 6} key={bank.title}>
                  <SummaryTableCard title={bank.title} data={bank.rows} />
                </Grid>
              ))
            : null}
        </Grid>
      </S.Row>

      <S.Row>
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox checked={checked} onChange={handleChange} />
          <Typography variant="body2">
            I understand that this mint order will be initiated only after a transfer of the corresponding amount in CAD
            is received at the selected bank.
          </Typography>
        </Stack>
      </S.Row>

      <S.Row>
        <Button variant="contained" fullWidth size="large" onClick={onProceed} disabled={!checked}>
          Proceed
        </Button>
      </S.Row>
    </Stack>
  )
}

export default PaymentInstructions
